// useAdminNotifications.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";

const MANAGER_ROLES = [2, 3];
const SUPERVISOR_ROLE = 4;

const canUseAdminNotifications = (roleId) =>
  MANAGER_ROLES.includes(roleId) || roleId === SUPERVISOR_ROLE;

// A supervisora só enxerga o que é do time dela: atraso de entrada/saída,
// break estourado e checklist. Square, material, pedidos de edição e afins
// continuam exclusivos da gerência.
export const SUPERVISOR_NOTIF_TYPES = [
  "NO_CLOCK_IN",
  "LATE",
  "BREAK_30",
  "MISSING_CLOCKOUT",
  "CHECKLIST_OPENING_MISSING",
  "CHECKLIST_OPENING_INCOMPLETE",
  "CHECKLIST_CLOSING_MISSING",
  "CHECKLIST_CLOSING_INCOMPLETE",
  "CHECKLIST_REPORT_MISSING",
  "CHECKLIST_REPORT_INCOMPLETE",
];

export const useAdminNotifications = (user) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Grupo da supervisora — define o recorte das notificações dela.
  const isSupervisor = user?.role_id === SUPERVISOR_ROLE;
  const [groupId, setGroupId] = useState(null);

  useEffect(() => {
    if (!isSupervisor) {
      setGroupId(null);
      return undefined;
    }
    let alive = true;
    api
      .get("/supervisor/group")
      .then((r) => { if (alive) setGroupId(r.data?.group_id ?? null); })
      .catch(() => { if (alive) setGroupId(null); });
    return () => { alive = false; };
  }, [isSupervisor, user?.id]);

  const refetch = useCallback(async () => {
    if (!canUseAdminNotifications(user?.role_id)) return;
    // Sem grupo resolvido ainda não dá para filtrar — melhor não mostrar nada
    // do que mostrar notificações de outro time.
    if (isSupervisor && !groupId) return;

    setLoading(true);
    try {
      const res = await api.get("/admin/notifications", {
        params: isSupervisor ? { group_id: groupId } : undefined,
      });
      const list = res.data?.notifications || [];
      setItems(
        isSupervisor
          ? list.filter((n) => SUPERVISOR_NOTIF_TYPES.includes(n.notification_type))
          : list
      );
    } catch (e) {
      console.error("Error fetching admin notifications:", e);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [user?.role_id, isSupervisor, groupId]);

  const refresh = useCallback(async () => {
    if (!canUseAdminNotifications(user?.role_id)) return;
    if (isSupervisor && !groupId) return;

    // evita refresh concorrente
    if (refreshing) return;

    setRefreshing(true);
    try {
      await api.post("/admin/notifications/refresh", {});
    } catch (e) {
      console.error("Error refreshing admin notifications:", e);
      // mesmo se falhar, ainda tenta pegar as atuais
    } finally {
      setRefreshing(false);
    }

    await refetch();
  }, [user?.role_id, isSupervisor, groupId, refetch, refreshing]);

  // Carga inicial. Fica aqui e não no Dashboard porque no caso da supervisora
  // só dá para buscar depois que o group_id resolve — a chave garante um
  // refresh por contexto (usuária + grupo), sem repetir a cada render.
  const didInitRef = useRef(null);
  useEffect(() => {
    if (!canUseAdminNotifications(user?.role_id)) return;
    if (isSupervisor && !groupId) return;

    const key = `${user?.id}:${groupId ?? "all"}`;
    if (didInitRef.current === key) return;
    didInitRef.current = key;
    refresh();
  }, [user?.id, user?.role_id, isSupervisor, groupId, refresh]);

  const count = useMemo(() => (items || []).length, [items]);

  const resolve = useCallback(
    async (notification_key) => {
      if (!notification_key) return;
      if (!canUseAdminNotifications(user?.role_id)) return;

      try {
        await api.post("/admin/notifications/resolve", {
          notification_key,
          reason: "resolved",
        });
        await refetch();
      } catch (e) {
        console.error("Error resolving notification:", e);
      }
    },
    [refetch, user?.role_id]
  );

  return {
    notifications: items,
    count,
    loading,
    refreshing,
    refetch,
    refresh,
    resolve,
  };
};
